import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Radio, Crosshair, Shield, Wifi, Lock, Signal } from 'lucide-react';
import UmbrellaLogo from '@/components/UmbrellaLogo';
import LeonDialogue from '@/components/LeonDialogue';
import { CINEMATIC_INTRO } from '@/config/missionContent';

const BRIEFING_LINES = CINEMATIC_INTRO.briefingLines;
const SYS_TICKER = CINEMATIC_INTRO.sysTicker;
const DATA_FRAGMENTS = CINEMATIC_INTRO.dataFragments;

/* ═══════════════════════════════════════════════
   TYPED LINE — character-by-character reveal
   ═══════════════════════════════════════════════ */
function TypedLine({ text, speed = 25, delay = 0, className = '', onDone }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started || !text) return;
    let i = 0;
    setDisplayed('');
    const iv = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(iv); onDone?.(); }
    }, speed);
    return () => clearInterval(iv);
  }, [started, text, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!started) return null;
  return (
    <span className={className}>
      {displayed}
      {displayed.length < text.length && (
        <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }} className="text-green-400">_</motion.span>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════
   SIGNAL ACQUISITION — opening sequence
   ═══════════════════════════════════════════════ */
function SignalAcquisition({ onComplete }) {
  const [phase, setPhase] = useState('static'); // static → acquiring → locked

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('acquiring'), 600);
    const t2 = setTimeout(() => setPhase('locked'), 1600);
    const t3 = setTimeout(() => onComplete?.(), 2100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <motion.div
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: '#000' }}
    >
      {/* Static noise */}
      {phase === 'static' && (
        <motion.div
          animate={{ opacity: [0.15, 0.08, 0.12, 0.06] }}
          transition={{ duration: 0.15, repeat: Infinity }}
          className="absolute inset-0"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")',
            backgroundSize: '200px',
          }}
        />
      )}

      {/* CRT scanlines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, transparent 4px)',
      }} />

      <div className="relative z-10 flex flex-col items-center">
        {/* Signal icon */}
        <motion.div
          animate={phase === 'acquiring' ? { scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] } : phase === 'locked' ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.5, repeat: phase === 'acquiring' ? Infinity : 0 }}
          className="mb-6"
        >
          <Signal
            size={32}
            className={phase === 'locked' ? 'text-green-400' : phase === 'acquiring' ? 'text-yellow-500' : 'text-red-600'}
            style={{ filter: phase === 'locked' ? 'drop-shadow(0 0 10px rgba(0,255,65,0.5))' : 'none' }}
          />
        </motion.div>

        {/* Status text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-terminal text-sm tracking-[6px] mb-4 text-center"
          style={{ color: phase === 'locked' ? '#00ff41' : phase === 'acquiring' ? '#eab308' : '#ef4444' }}
        >
          {phase === 'locked' ? CINEMATIC_INTRO.signal.locked : phase === 'acquiring' ? CINEMATIC_INTRO.signal.acquiring : CINEMATIC_INTRO.signal.incoming}
        </motion.p>

        {/* Signal bars */}
        <div className="flex items-end justify-center gap-1">
          {[6, 10, 14, 18, 22].map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0.15 }}
              animate={{
                opacity: phase === 'locked' ? 1
                  : phase === 'acquiring' ? (i < 3 ? [0.3, 0.8, 0.3] : 0.15)
                  : 0.15,
                background: phase === 'locked' ? '#00ff41' : phase === 'acquiring' && i < 3 ? '#eab308' : 'rgba(255,255,255,0.15)',
              }}
              transition={{ duration: 0.3, delay: phase === 'locked' ? i * 0.08 : 0, repeat: phase === 'acquiring' ? Infinity : 0, repeatDelay: 0.5 }}
              className="w-2 rounded-sm"
              style={{ height: h }}
            />
          ))}
        </div>
      </div>

      {/* Screen border */}
      <motion.div
        animate={{
          borderColor: phase === 'locked' ? 'rgba(0,255,65,0.3)' : phase === 'acquiring' ? 'rgba(234,179,8,0.2)' : 'rgba(239,68,68,0.2)',
        }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 pointer-events-none"
        style={{ border: '1px solid' }}
      />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   BACKGROUND RADAR — large rotating sweep
   ═══════════════════════════════════════════════ */
function BackgroundRadar() {
  return (
    <div className="fixed inset-0 pointer-events-none z-[1] flex items-center justify-center overflow-hidden">
      <svg viewBox="0 0 400 400" className="w-[600px] h-[600px] opacity-[0.04]">
        <circle cx="200" cy="200" r="180" fill="none" stroke="#00ff41" strokeWidth="0.5" />
        <circle cx="200" cy="200" r="120" fill="none" stroke="#00ff41" strokeWidth="0.3" />
        <circle cx="200" cy="200" r="60" fill="none" stroke="#00ff41" strokeWidth="0.3" />
        <line x1="20" y1="200" x2="380" y2="200" stroke="#00ff41" strokeWidth="0.2" />
        <line x1="200" y1="20" x2="200" y2="380" stroke="#00ff41" strokeWidth="0.2" />
        <motion.g
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '200px 200px' }}
        >
          <line x1="200" y1="200" x2="200" y2="20" stroke="#00ff41" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
          {/* Sweep trail */}
          <path d="M200,200 L200,20 A180,180 0 0,1 260,30 Z" fill="url(#sweepGrad)" />
        </motion.g>
        <defs>
          <linearGradient id="sweepGrad" gradientTransform="rotate(10, 200, 200)">
            <stop offset="0%" stopColor="#00ff41" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#00ff41" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DATA STREAMS — faint vertical scrolling chars
   ═══════════════════════════════════════════════ */
function DataStreams() {
  const streams = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      x: 5 + (i / 12) * 90,
      chars: Array.from({ length: 20 }, () => String.fromCharCode(0x30A0 + Math.random() * 96)).join('\n'),
      speed: 8 + Math.random() * 6,
      delay: Math.random() * 5,
      opacity: 0.03 + Math.random() * 0.03,
    })), []
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {streams.map((s, i) => (
        <motion.div
          key={i}
          className="absolute top-0"
          style={{
            left: `${s.x}%`,
            fontFamily: 'VT323, monospace',
            fontSize: 11,
            color: '#00ff41',
            opacity: s.opacity,
            lineHeight: 1.4,
            whiteSpace: 'pre',
          }}
          animate={{ y: ['-50%', '100%'] }}
          transition={{ duration: s.speed, delay: s.delay, repeat: Infinity, ease: 'linear' }}
        >
          {s.chars}
        </motion.div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FLOATING DATA FRAGMENTS
   ═══════════════════════════════════════════════ */
function FloatingFragments() {
  const frags = useMemo(() =>
    Array.from({ length: 15 }, (_, i) => ({
      text: DATA_FRAGMENTS[i % DATA_FRAGMENTS.length],
      x: Math.random() * 90 + 5,
      y: Math.random() * 80 + 10,
      duration: 6 + Math.random() * 8,
      delay: Math.random() * 10,
      drift: (Math.random() - 0.5) * 40,
    })), []
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden">
      {frags.map((f, i) => (
        <motion.span
          key={i}
          className="absolute font-terminal text-xs tracking-[3px]"
          style={{ left: `${f.x}%`, top: `${f.y}%`, color: 'rgba(0,255,65,0.08)' }}
          animate={{
            opacity: [0, 0.12, 0.08, 0],
            x: [0, f.drift],
            y: [0, -20],
          }}
          transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, repeatDelay: 3 }}
        >
          {f.text}
        </motion.span>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   RED ALARM SWEEP — rotating emergency lights
   ═══════════════════════════════════════════════ */
function AlarmSweep() {
  return (
    <>
      <motion.div
        animate={{ opacity: [0, 0.06, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        className="fixed inset-0 pointer-events-none z-[2]"
        style={{ background: 'linear-gradient(90deg, rgba(204,0,0,0.15), transparent 30%, transparent 70%, rgba(204,0,0,0.15))' }}
      />
      <motion.div
        animate={{ opacity: [0, 0.04, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="fixed inset-0 pointer-events-none z-[2]"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(204,0,0,0.08) 100%)' }}
      />
    </>
  );
}

/* ═══════════════════════════════════════════════
   TOP HUD BAR
   ═══════════════════════════════════════════════ */
function TopBar() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <motion.div
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 2.5, duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-20 flex items-center h-8 px-4 bg-black/80 border-b border-green-900/30 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2">
        <Lock size={9} className="text-green-700" />
        <span className="text-xs font-terminal text-green-700 tracking-[2px]">{CINEMATIC_INTRO.topBar.secureChannel}</span>
      </div>
      <div className="mx-3 w-px h-3 bg-green-900/30" />
      <div className="flex items-center gap-1.5">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-1.5 h-1.5 rounded-full bg-green-500"
        />
        <span className="text-xs font-terminal text-green-600 tracking-wider">{CINEMATIC_INTRO.topBar.encrypted}</span>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-2">
        <Wifi size={9} className="text-green-700" />
        <div className="flex items-end gap-px">
          {[3, 5, 7, 9].map((h, i) => (
            <div key={i} className="w-1 rounded-sm" style={{ height: h, background: 'rgba(0,255,65,0.5)' }} />
          ))}
        </div>
        <span className="text-xs font-terminal text-green-800 tracking-wider tabular-nums ml-2">
          {time.toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   BOTTOM TICKER — scrolling system messages
   ═══════════════════════════════════════════════ */
function BottomTicker() {
  const tickerText = SYS_TICKER.map(m => `  ◈  ${m}`).join('');

  return (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 3, duration: 0.5 }}
      className="fixed bottom-0 left-0 right-0 z-20 h-6 bg-black/80 border-t border-green-900/20 overflow-hidden flex items-center"
    >
      <motion.span
        animate={{ x: ['100%', '-100%'] }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        className="text-xs font-terminal text-green-800 tracking-wider whitespace-nowrap"
      >
        {tickerText}{tickerText}
      </motion.span>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   HUD FRAME — corners + side markers enhanced
   ═══════════════════════════════════════════════ */
function HUDFrame() {
  return (
    <>
      {/* Corner brackets */}
      {[
        'top-0 left-0 border-t-2 border-l-2',
        'top-0 right-0 border-t-2 border-r-2',
        'bottom-0 left-0 border-b-2 border-l-2',
        'bottom-0 right-0 border-b-2 border-r-2',
      ].map((pos, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.5 + i * 0.1, type: 'spring' }}
          className={`absolute ${pos} w-6 h-6 border-red-800/40 pointer-events-none`}
        />
      ))}

      {/* Left side data */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-none"
      >
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-red-800/30" />
        <Crosshair size={8} className="text-red-800/30" />
        <span className="font-terminal text-xs text-green-900 tracking-wider" style={{ writingMode: 'vertical-rl' }}>47.3°N</span>
        <div className="w-px h-8 bg-gradient-to-t from-transparent to-red-800/30" />
      </motion.div>

      {/* Right side data */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-none"
      >
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-red-800/30" />
        <Radio size={8} className="text-red-800/30" />
        <span className="font-terminal text-xs text-green-900 tracking-wider" style={{ writingMode: 'vertical-rl' }}>8.5°E</span>
        <div className="w-px h-8 bg-gradient-to-t from-transparent to-red-800/30" />
      </motion.div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   CLASSIFICATION STAMP — slams down mid-briefing
   ═══════════════════════════════════════════════ */
function ClassifiedStamp({ visible }) {
  if (!visible) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
      {/* Impact rings */}
      {[0, 0.1, 0.2].map((delay, i) => (
        <motion.div
          key={i}
          initial={{ scale: 0.3, opacity: 0.5 }}
          animate={{ scale: 3, opacity: 0 }}
          transition={{ duration: 0.6, delay, ease: 'easeOut' }}
          className="absolute rounded-full"
          style={{ width: 120, height: 60, border: '1px solid rgba(239,68,68,0.3)' }}
        />
      ))}
      {/* Stamp text */}
      <motion.div
        initial={{ scale: 3, opacity: 0, rotate: -15 }}
        animate={{ scale: [3, 0.95, 1], opacity: [0, 1, 0.05], rotate: -12 }}
        transition={{ duration: 0.4, times: [0, 0.6, 1], ease: [0.16, 1, 0.3, 1] }}
        className="absolute"
      >
        <div className="px-8 py-3 border-4 border-red-600 rounded-sm" style={{ transform: 'rotate(-12deg)' }}>
          <span className="font-terminal text-3xl md:text-4xl tracking-[8px] text-red-600 font-bold">
            {CINEMATIC_INTRO.stamp}
          </span>
        </div>
      </motion.div>
      {/* Red flash on impact */}
      <motion.div
        initial={{ opacity: 0.2 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0"
        style={{ background: 'rgba(239,68,68,0.08)' }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SWEEP LINE — decorative divider
   ═══════════════════════════════════════════════ */
function SweepLine() {
  return (
    <motion.div
      initial={{ scaleX: 0, opacity: 0 }}
      animate={{ scaleX: 1, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="origin-left"
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gradient-to-r from-red-800/60 via-green-800/30 to-transparent" />
        <motion.div
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          className="w-1 h-1 bg-green-600/40 rotate-45"
        />
        <div className="flex-1 h-px bg-gradient-to-l from-red-800/60 via-green-800/30 to-transparent" />
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   GLITCH TEXT — RGB offset text
   ═══════════════════════════════════════════════ */
function GlitchText({ children, className }) {
  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative z-10">{children}</span>
      <span className="absolute inset-0 text-red-600/40 animate-glitch-1" aria-hidden="true" style={{ clipPath: 'inset(20% 0 40% 0)' }}>{children}</span>
      <span className="absolute inset-0 text-cyan-500/30 animate-glitch-2" aria-hidden="true" style={{ clipPath: 'inset(60% 0 10% 0)' }}>{children}</span>
    </span>
  );
}

/* ═══════════════════════════════════════════════
   COMM LINK — pre-Leon connection animation
   ═══════════════════════════════════════════════ */
function CommLink({ visible, onConnected }) {
  const [phase, setPhase] = useState('connecting'); // connecting → connected

  useEffect(() => {
    if (!visible) return;
    setPhase('connecting');
    const t = setTimeout(() => { setPhase('connected'); onConnected?.(); }, 2000);
    return () => clearTimeout(t);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center mt-6"
    >
      <div className="inline-flex items-center gap-3 px-5 py-2 rounded-sm"
        style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,65,0.15)' }}
      >
        <motion.div
          animate={phase === 'connecting' ? { opacity: [0.3, 1, 0.3] } : {}}
          transition={{ duration: 0.8, repeat: Infinity }}
        >
          <Radio size={14} className={phase === 'connected' ? 'text-green-400' : 'text-yellow-500'} />
        </motion.div>
        <span className="font-terminal text-xs tracking-[3px]"
          style={{ color: phase === 'connected' ? '#00ff41' : '#eab308' }}
        >
          {phase === 'connected' ? CINEMATIC_INTRO.commLink.established : CINEMATIC_INTRO.commLink.incoming}
        </span>
        {/* Connection bars */}
        <div className="flex items-center gap-px ml-1">
          {[0, 1, 2, 3].map(i => (
            <motion.div
              key={i}
              animate={{
                background: phase === 'connected' ? '#00ff41'
                  : i <= 1 ? ['rgba(234,179,8,0.3)', 'rgba(234,179,8,0.8)', 'rgba(234,179,8,0.3)']
                  : 'rgba(255,255,255,0.1)',
              }}
              transition={{ duration: 0.5, repeat: phase === 'connecting' ? Infinity : 0, delay: i * 0.15 }}
              className="w-1.5 h-3 rounded-sm"
            />
          ))}
        </div>
      </div>
      {/* Static noise on connection */}
      {phase === 'connecting' && (
        <motion.div
          animate={{ opacity: [0, 0.03, 0] }}
          transition={{ duration: 0.2, repeat: Infinity }}
          className="fixed inset-0 pointer-events-none z-40"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")',
            backgroundSize: '200px',
          }}
        />
      )}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN CINEMATIC INTRO
   ═══════════════════════════════════════════════ */
export default function CinematicIntro({ onComplete }) {
  const [signalDone, setSignalDone] = useState(false);
  const [visibleLines, setVisibleLines] = useState([]);
  const [showCommLink, setShowCommLink] = useState(false);
  const [showLeon, setShowLeon] = useState(false);
  const [leonDismissed, setLeonDismissed] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [screenShake, setScreenShake] = useState(false);

  // Reset on mount
  useEffect(() => {
    setSignalDone(false);
    setVisibleLines([]);
    setShowCommLink(false);
    setShowLeon(false);
    setLeonDismissed(false);
    setShowStamp(false);
  }, []);

  const handleSignalDone = useCallback(() => setSignalDone(true), []);

  // Reveal lines after signal acquired
  useEffect(() => {
    if (!signalDone) return;
    const timers = BRIEFING_LINES.map((line, i) =>
      setTimeout(() => {
        setVisibleLines(prev => [...prev, i]);
        // Shake on stamp
        if (line.type === 'stamp') {
          setShowStamp(true);
          setScreenShake(true);
          setTimeout(() => setScreenShake(false), 300);
        }
      }, line.delay - 2000) // offset by signal acquisition time
    );
    // Show comm link before Leon
    const commTimer = setTimeout(() => setShowCommLink(true), 12800);
    const leonTimer = setTimeout(() => setShowLeon(true), 15000);
    return () => { timers.forEach(clearTimeout); clearTimeout(commTimer); clearTimeout(leonTimer); };
  }, [signalDone]);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setLeonDismissed(true);
    // Show "MISSION PARAMETERS LOADED" then button
  }, []);

  const renderLine = (line, i) => {
    if (!visibleLines.includes(i)) return null;

    switch (line.type) {
      case 'header':
        return (
          <motion.div key={i}
            initial={{ opacity: 0, scale: 1.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center py-2"
          >
            <GlitchText className="font-terminal text-xl md:text-2xl tracking-[10px] text-red-500 text-glow-red">
              {line.text}
            </GlitchText>
          </motion.div>
        );

      case 'sub':
        return (
          <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ duration: 0.5 }}>
            <TypedLine
              text={line.text}
              speed={20}
              className="block text-center text-xs font-terminal text-green-700 tracking-[5px]"
            />
          </motion.div>
        );

      case 'divider':
        return <SweepLine key={i} />;

      case 'stamp':
        return null; // Handled by ClassifiedStamp overlay

      case 'field':
        return (
          <motion.div key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4"
          >
            <span className="text-xs font-terminal tracking-[3px] text-green-800 uppercase w-24 text-right">{line.label}</span>
            <div className="w-px h-4 bg-green-800/30" />
            {line.highlight ? (
              <span className="font-terminal tracking-[4px] text-lg">
                {/* Letter-by-letter red glow name */}
                {line.value.split('').map((char, ci) => (
                  <motion.span
                    key={ci}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: ci * 0.08, duration: 0.3 }}
                    className="text-red-400 font-bold inline-block"
                    style={{ textShadow: '0 0 10px rgba(239,68,68,0.5), 0 0 20px rgba(239,68,68,0.2)' }}
                  >
                    {char}
                  </motion.span>
                ))}
                {/* Scan line on name */}
                <motion.div
                  initial={{ left: 0, opacity: 0.5 }}
                  animate={{ left: '100%', opacity: 0 }}
                  transition={{ duration: 0.8, delay: line.value.length * 0.08 }}
                  className="absolute h-full w-[2px] top-0"
                  style={{ background: 'rgba(239,68,68,0.6)' }}
                />
              </span>
            ) : (
              <TypedLine
                text={line.value}
                speed={35}
                className="font-terminal tracking-[4px] text-base text-green-500 text-glow-green"
              />
            )}
          </motion.div>
        );

      case 'narrative':
        return (
          <motion.div key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <TypedLine
              text={line.text}
              speed={35}
              className="text-gray-300 font-special text-lg md:text-xl text-center leading-relaxed block"
            />
          </motion.div>
        );

      case 'emphasis':
        return (
          <motion.div key={i}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-center"
          >
            <motion.span
              animate={{ textShadow: ['0 0 10px rgba(255,255,255,0.1)', '0 0 25px rgba(255,255,255,0.2)', '0 0 10px rgba(255,255,255,0.1)'] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="font-special text-xl md:text-2xl italic text-gray-100 tracking-wide"
            >
              {line.text}
            </motion.span>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      animate={screenShake ? { x: [0, -4, 4, -3, 2, 0] } : {}}
      transition={{ duration: 0.3 }}
      className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden"
      data-testid="cinematic-intro"
    >
      {/* Background with slow zoom */}
      <motion.div
        initial={{ scale: 1 }}
        animate={{ scale: 1.08 }}
        transition={{ duration: 30, ease: 'linear' }}
        className="fixed inset-0 z-0"
      >
        <img src={CINEMATIC_INTRO.bgImage} alt="" className="w-full h-full object-cover opacity-10" />
      </motion.div>
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-black via-black/40 to-black" />
      <div className="fixed inset-0 z-0 bg-gradient-to-t from-red-950/15 via-transparent to-transparent" />

      {/* CRT Scanlines */}
      <div className="fixed inset-0 pointer-events-none z-[50]" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, transparent 4px)',
      }} />

      {/* Background effects */}
      {signalDone && (
        <>
          <BackgroundRadar />
          <DataStreams />
          <FloatingFragments />
          <AlarmSweep />
          <TopBar />
          <BottomTicker />
        </>
      )}

      {/* Ambient floating particles */}
      <div className="fixed inset-0 z-[3] pointer-events-none overflow-hidden">
        {Array.from({ length: 25 }).map((_, i) => (
          <motion.div key={i}
            initial={{ x: `${Math.random() * 100}vw`, y: '110vh', opacity: 0 }}
            animate={{ y: '-10vh', opacity: [0, 0.2, 0] }}
            transition={{ duration: 10 + Math.random() * 10, delay: Math.random() * 8, repeat: Infinity }}
            className="absolute"
          >
            <div className="w-px h-8 bg-gradient-to-t from-transparent via-red-800/20 to-transparent" />
          </motion.div>
        ))}
      </div>

      {/* Signal Acquisition */}
      <AnimatePresence>
        {!signalDone && <SignalAcquisition onComplete={handleSignalDone} />}
      </AnimatePresence>

      {/* Ghost Umbrella logo */}
      {signalDone && (
        <motion.div
          initial={{ opacity: 0, scale: 0.3, rotate: -90 }}
          animate={{ opacity: 0.05, scale: 1, rotate: 0 }}
          transition={{ duration: 4, ease: 'easeOut' }}
          className="absolute pointer-events-none z-[3]"
        >
          <UmbrellaLogo size={400} />
        </motion.div>
      )}

      {/* Main content container */}
      {signalDone && (
        <div className="max-w-xl w-full relative z-10 px-4 py-8">
          <div className="relative p-8 md:p-12">
            <HUDFrame />
            <ClassifiedStamp visible={showStamp} />

            <div className="space-y-5">
              {BRIEFING_LINES.map((line, i) => renderLine(line, i))}
            </div>

            {/* Comm link before Leon */}
            <CommLink
              visible={showCommLink && !showLeon && !leonDismissed}
              onConnected={() => {}}
            />

            {/* Post-Leon: mission loaded + proceed */}
            {leonDismissed && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="mt-8 text-center"
              >
                {/* Mission loaded confirmation */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-sm"
                  style={{ background: 'rgba(0,255,65,0.05)', border: '1px solid rgba(0,255,65,0.15)' }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                  >
                    <Shield size={12} className="text-green-500" />
                  </motion.div>
                  <TypedLine
                    text={CINEMATIC_INTRO.missionLoaded}
                    speed={30}
                    delay={400}
                    className="font-terminal text-xs tracking-[3px] text-green-500"
                  />
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="text-gray-400 font-special text-lg mb-8"
                >
                  {CINEMATIC_INTRO.readyPrompt}
                </motion.p>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 }}
                  whileHover={{ scale: 1.03, boxShadow: '0 0 35px rgba(204,0,0,0.35)', letterSpacing: '8px' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onComplete?.()}
                  className="group relative px-10 py-4 bg-gradient-to-r from-red-950/60 via-red-900/50 to-red-950/60 border-2 border-red-600/60 text-red-300 font-terminal text-lg tracking-[6px] uppercase transition-all duration-300 hover:border-red-500/80 overflow-hidden"
                  data-testid="begin-mission-btn"
                >
                  {/* Border pulse */}
                  <motion.div
                    animate={{
                      boxShadow: ['0 0 10px rgba(204,0,0,0.1)', '0 0 25px rgba(204,0,0,0.25)', '0 0 10px rgba(204,0,0,0.1)'],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 pointer-events-none"
                  />
                  {/* Sweeping shimmer */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/10 to-transparent pointer-events-none"
                    animate={{ x: ['-200%', '200%'] }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
                  />
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    {CINEMATIC_INTRO.beginButton}
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>
      )}

      {showLeon && (
        <LeonDialogue
          message={CINEMATIC_INTRO.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}
    </motion.div>
  );
}
